import { useForm } from 'react-hook-form';

import type {
  Address as ColumnAddress,
  BusinessAssociatedPerson as ColumnBusinessAssociatedPerson,
  BusinessDetails as ColumnBusinessDetails,
  CreateBusinessEntity as ColumnCreateBusinessEntity,
  CreatePersonEntity as ColumnCreatePersonEntity,
  Entity as ColumnEntity,
  PersonDetails as ColumnPersonDetails,
  UpdateBusinessEntity as ColumnUpdateBusinessEntity,
  UpdatePersonEntity as ColumnUpdatePersonEntity,
} from '@column/api';

export enum EntityFormType {
  Person = 'person',
  Business = 'business',
}

type FormAddress = {
  line1?: ColumnAddress['line_1'] | null;
  line2?: ColumnAddress['line_2'] | null;
  city?: ColumnAddress['city'] | null;
  state?: ColumnAddress['state'] | null;
  postalCode?: ColumnAddress['postal_code'] | null;
  countryCode?: ColumnAddress['country_code'] | null;
};

export type IdentificationDocument = {
  // eslint-disable-next-line id-blacklist
  number?: string;
  countryCode?: string;
};

export interface PersonEntityFormValues {
  id?: string;
  firstName?: string;
  lastName?: string;
  middleName?: string;
  ssn?: string;
  dateOfBirth?: string;
  email?: string;
  phoneNumber?: string;
  address?: FormAddress;
  isRoot?: boolean;
  passport?: IdentificationDocument;
  driversLicense?: IdentificationDocument;
  nationalId?: IdentificationDocument;
  temporaryData?: { isCitizen?: boolean };
}

export interface BeneficialOwnerFormValues extends PersonEntityFormValues {
  isControlPerson?: boolean;
  isBeneficialOwner?: boolean;
  ownershipPercentage?: number | string;
  jobTitle?: string;
}

export interface BusinessEntityFormValues {
  id?: string;
  businessName?: string;
  ein?: string;
  einPending?: boolean;
  website?: string;
  isRoot?: boolean;
  address?: FormAddress;
  beneficialOwners?: BeneficialOwnerFormValues[];
}

const createEmptyAddress = (): FormAddress => ({
  line1: '',
  line2: '',
  city: '',
  state: '',
  postalCode: '',
  countryCode: '',
});

const createEmptyRequestAddress = (): ColumnAddress => ({
  line_1: '',
  line_2: '',
  city: '',
  state: '',
  postal_code: '',
  country_code: '',
});

const toFormString = (value: unknown): string | undefined => {
  if (value === undefined || value === null) return undefined;
  return value.toString();
};

const apiAddressToFormAddress = (address?: ColumnAddress): FormAddress | undefined => {
  if (!address) return undefined;
  return {
    line1: address.line_1,
    line2: address.line_2,
    city: address.city,
    state: address.state,
    postalCode: address.postal_code,
    countryCode: address.country_code,
  };
};

const apiIdentificationToFormIdentification = (doc: unknown): IdentificationDocument | undefined => {
  if (!doc || typeof doc !== 'object') return undefined;
  const value = doc as Record<string, unknown>;
  const docNumber = toFormString(value.number);
  const countryCode = toFormString(value.country_code);

  if (!docNumber && !countryCode) return undefined;

  return {
    // eslint-disable-next-line id-blacklist
    number: docNumber,
    countryCode,
  };
};

const apiPersonDetailsToFormValues = (details: ColumnPersonDetails): PersonEntityFormValues => ({
  firstName: details.first_name,
  middleName: details.middle_name,
  lastName: details.last_name,
  ssn: toFormString(details.ssn),
  dateOfBirth: details.date_of_birth,
  email: details.email,
  phoneNumber: details.phone_number,
  address: apiAddressToFormAddress(details.address),
  passport: apiIdentificationToFormIdentification(details.passport),
  driversLicense: apiIdentificationToFormIdentification(details.drivers_license),
  nationalId: apiIdentificationToFormIdentification(details.national_id),
});

const apiBeneficialOwnerToFormValues = (owner: ColumnPersonDetails): BeneficialOwnerFormValues => ({
  ...apiPersonDetailsToFormValues(owner),
  isControlPerson: owner.is_control_person,
  isBeneficialOwner: owner.is_beneficial_owner,
  ownershipPercentage: owner.ownership_percentage,
  jobTitle: owner.job_title,
});

const apiBusinessDetailsToFormValues = (details: ColumnBusinessDetails): BusinessEntityFormValues => ({
  businessName: details.business_name,
  ein: toFormString(details.ein),
  einPending: details.ein_pending ?? false,
  website: details.website,
  address: apiAddressToFormAddress(details.address),
  beneficialOwners: details.beneficial_owners?.map(apiBeneficialOwnerToFormValues),
});

const trimValue = (value?: string | null) => {
  if (value === undefined || value === null) return undefined;
  const trimmed = value.toString().trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const normalizeAddress = (address?: FormAddress): ColumnAddress | undefined => {
  if (!address) return undefined;
  const line1 = trimValue(address.line1);
  const line2 = trimValue(address.line2);
  const city = trimValue(address.city);
  const state = trimValue(address.state);
  const postalCode = trimValue(address.postalCode);
  const countryCode = trimValue(address.countryCode);

  if (!line1 && !line2 && !city && !state && !postalCode && !countryCode) return undefined;

  return {
    ...(line1 && { line_1: line1 }),
    ...(line2 && { line_2: line2 }),
    ...(city && { city }),
    ...(state && { state }),
    ...(postalCode && { postal_code: postalCode }),
    ...(countryCode && { country_code: countryCode }),
  } as ColumnAddress;
};

const createEmptyIdentification = (): IdentificationDocument => ({
  // eslint-disable-next-line id-blacklist
  number: '',
  countryCode: '',
});

const normalizeIdentification = (doc?: IdentificationDocument) => {
  if (!doc) return undefined;
  const docNumber = trimValue(doc.number);
  const countryCode = trimValue(doc.countryCode);

  if (!docNumber && !countryCode) return undefined;

  return {
    // eslint-disable-next-line id-blacklist
    ...(docNumber && { number: docNumber }),
    ...(countryCode && { country_code: countryCode }),
  };
};

export const normalizeDateOfBirth = (date: unknown): string | undefined => {
  if (typeof date !== 'string') return undefined;
  const trimmed = date.trim();
  if (!trimmed) return undefined;
  return /^\d{4}-\d{2}-\d{2}$/.test(trimmed) ? trimmed : undefined;
};

const inferCitizenFromIdentifiers = (entry: Partial<BeneficialOwnerFormValues | PersonEntityFormValues>): boolean => {
  if (entry.ssn && typeof entry.ssn === 'string' && entry.ssn.trim()) {
    return true;
  }
  if (
    (entry.passport && entry.passport.number) ||
    (entry.driversLicense && entry.driversLicense.number) ||
    (entry.nationalId && entry.nationalId.number)
  ) {
    return false;
  }
  return true;
};

export const createDefaultPersonFormValues = (): PersonEntityFormValues => ({
  id: undefined,
  firstName: '',
  middleName: '',
  lastName: '',
  ssn: '',
  dateOfBirth: '',
  email: '',
  phoneNumber: '',
  passport: createEmptyIdentification(),
  driversLicense: createEmptyIdentification(),
  nationalId: createEmptyIdentification(),
  address: createEmptyAddress(),
  temporaryData: { isCitizen: true },
});

export const createDefaultBeneficialOwner = (): BeneficialOwnerFormValues => ({
  ...createDefaultPersonFormValues(),
  temporaryData: { isCitizen: true },
  isControlPerson: undefined,
  isBeneficialOwner: true,
  ownershipPercentage: undefined,
  jobTitle: '',
});

export const createDefaultBusinessFormValues = (): BusinessEntityFormValues => ({
  id: undefined,
  businessName: '',
  ein: '',
  einPending: false,
  website: '',
  address: createEmptyAddress(),
  beneficialOwners: [],
});

export const createInitialBusinessFormState = (): BusinessEntityFormValues => ({
  ...createDefaultBusinessFormValues(),
  beneficialOwners: [],
});

const buildPersonUpdateRequestBody = (values: PersonEntityFormValues): ColumnUpdatePersonEntity => {
  const isCitizen = values.temporaryData?.isCitizen !== false;

  const firstName = trimValue(values.firstName);
  const middleName = trimValue(values.middleName);
  const lastName = trimValue(values.lastName);
  const ssn = isCitizen ? trimValue(values.ssn) : undefined;
  const dateOfBirth = normalizeDateOfBirth(values.dateOfBirth);
  const email = trimValue(values.email);
  const phoneNumber = trimValue(values.phoneNumber);
  const address = normalizeAddress(values.address);
  const passport = !isCitizen ? normalizeIdentification(values.passport) : undefined;
  const driversLicense = !isCitizen ? normalizeIdentification(values.driversLicense) : undefined;
  const nationalId = !isCitizen ? normalizeIdentification(values.nationalId) : undefined;

  return {
    first_name: firstName ?? '',
    last_name: lastName ?? '',
    ...(middleName && { middle_name: middleName }),
    ...(ssn && { ssn }),
    ...(dateOfBirth && { date_of_birth: dateOfBirth }),
    ...(email && { email }),
    ...(phoneNumber && { phone_number: phoneNumber }),
    ...(address && { address }),
    ...(passport && { passport }),
    ...(driversLicense && { drivers_license: driversLicense }),
    ...(nationalId && { national_id: nationalId }),
  };
};

export const buildPersonUpdateRequest = (values: PersonEntityFormValues): ColumnUpdatePersonEntity =>
  buildPersonUpdateRequestBody(values);

export const buildPersonCreateRequest = (values: PersonEntityFormValues): ColumnCreatePersonEntity =>
  buildPersonUpdateRequestBody(values) as ColumnCreatePersonEntity;

const buildBeneficialOwnerBaseRequest = (values: BeneficialOwnerFormValues): ColumnBusinessAssociatedPerson => {
  const baseRequest = buildPersonUpdateRequestBody(values) as ColumnBusinessAssociatedPerson;

  const isControlPerson = values.isControlPerson;
  const isBeneficialOwner = values.isBeneficialOwner;

  let ownershipPercentage: number | undefined;
  if (values.ownershipPercentage !== undefined && values.ownershipPercentage !== null) {
    const numValue =
      typeof values.ownershipPercentage === 'string'
        ? values.ownershipPercentage.trim() === ''
          ? undefined
          : Number(values.ownershipPercentage)
        : Number(values.ownershipPercentage);

    if (numValue !== undefined && !isNaN(numValue) && numValue >= 0 && numValue <= 100) {
      ownershipPercentage = numValue;
    }
  }

  const jobTitle = trimValue(values.jobTitle);

  return {
    ...baseRequest,
    ...(isControlPerson !== undefined && { is_control_person: isControlPerson }),
    ...(isBeneficialOwner !== undefined && { is_beneficial_owner: isBeneficialOwner }),
    ...(ownershipPercentage !== undefined && { ownership_percentage: ownershipPercentage }),
    ...(jobTitle && { job_title: jobTitle }),
  };
};

export const buildBeneficialOwnerRequest = (
  values: BeneficialOwnerFormValues
): ColumnBusinessAssociatedPerson | undefined => {
  const request = buildBeneficialOwnerBaseRequest(values);
  return Object.keys(request).length > 0 ? request : undefined;
};

const buildBusinessRequestBody = (values: BusinessEntityFormValues): ColumnUpdateBusinessEntity => {
  const beneficialOwners =
    values.beneficialOwners
      ?.map((owner) => buildBeneficialOwnerRequest(owner))
      .filter((owner): owner is ColumnBusinessAssociatedPerson => Boolean(owner)) ?? [];

  const businessName = trimValue(values.businessName);
  const ein = trimValue(values.ein);
  const website = trimValue(values.website);
  const address = normalizeAddress(values.address);

  return {
    business_name: businessName ?? '',
    ...(ein && { ein }),
    ...(website && { website }),
    address: address ?? createEmptyRequestAddress(),
    ...(beneficialOwners.length > 0 && { beneficial_owners: beneficialOwners }),
  };
};

export const buildBusinessUpdateRequest = (values: BusinessEntityFormValues): ColumnUpdateBusinessEntity =>
  buildBusinessRequestBody(values);

export const buildBusinessCreateRequest = (
  values: BusinessEntityFormValues,
  opts?: { allowEinPending?: boolean }
): ColumnCreateBusinessEntity => {
  const einPending = opts?.allowEinPending !== false && values.einPending === true;
  const request = buildBusinessRequestBody({
    ...values,
    ein: einPending ? '' : values.ein,
  });

  return {
    ...request,
    ...(einPending && { ein_pending: true as unknown as ColumnCreateBusinessEntity['ein_pending'] }),
  } as ColumnCreateBusinessEntity;
};

export const applyPersonDefaults = (values: Partial<PersonEntityFormValues>): PersonEntityFormValues => {
  const base = createDefaultPersonFormValues();
  const merged: PersonEntityFormValues = {
    ...base,
    ...values,
    address: { ...base.address, ...(values.address ?? {}) },
    passport: { ...base.passport, ...(values.passport ?? {}) },
    driversLicense: { ...base.driversLicense, ...(values.driversLicense ?? {}) },
    nationalId: { ...base.nationalId, ...(values.nationalId ?? {}) },
  };

  merged.temporaryData = { isCitizen: inferCitizenFromIdentifiers(merged) };

  return merged;
};

export const applyBeneficialOwnerDefaults = (owner: Partial<BeneficialOwnerFormValues>): BeneficialOwnerFormValues => {
  const base = createDefaultBeneficialOwner();
  const merged: BeneficialOwnerFormValues = {
    ...base,
    ...owner,
    address: { ...base.address, ...(owner.address ?? {}) },
    passport: { ...base.passport, ...(owner.passport ?? {}) },
    driversLicense: { ...base.driversLicense, ...(owner.driversLicense ?? {}) },
    nationalId: { ...base.nationalId, ...(owner.nationalId ?? {}) },
  };

  merged.temporaryData = { isCitizen: inferCitizenFromIdentifiers(merged) };

  return merged;
};

export const applyBusinessDefaults = (values: Partial<BusinessEntityFormValues>): BusinessEntityFormValues => {
  const base = createDefaultBusinessFormValues();
  const merged: BusinessEntityFormValues = {
    ...base,
    ...values,
    address: { ...base.address, ...(values.address ?? {}) },
    beneficialOwners:
      values.beneficialOwners && values.beneficialOwners.length > 0
        ? values.beneficialOwners.map((owner) => applyBeneficialOwnerDefaults(owner))
        : [],
  };

  return merged;
};

export const getPersonFormValuesFromEntity = (entity: ColumnEntity | null | undefined): PersonEntityFormValues => {
  if (!entity?.person_details) {
    return applyPersonDefaults(createDefaultPersonFormValues());
  }

  return applyPersonDefaults({
    ...apiPersonDetailsToFormValues(entity.person_details),
    id: entity.id,
  });
};

export const getBusinessFormValuesFromEntity = (entity: ColumnEntity | null | undefined): BusinessEntityFormValues => {
  if (!entity?.business_details) {
    return applyBusinessDefaults(createDefaultBusinessFormValues());
  }

  return applyBusinessDefaults({
    ...apiBusinessDetailsToFormValues(entity.business_details),
    id: entity.id,
  });
};

export type EntityFormValues = PersonEntityFormValues | BusinessEntityFormValues;

export const createDefaultValuesForType = (type: EntityFormType): EntityFormValues =>
  type === EntityFormType.Person ? createDefaultPersonFormValues() : createDefaultBusinessFormValues();

export const useEntityForm = (type: EntityFormType = EntityFormType.Person, defaultValues?: EntityFormValues) =>
  useForm<EntityFormValues>({
    mode: 'onChange',
    defaultValues: defaultValues ?? createDefaultValuesForType(type),
    shouldUnregister: false,
  });
